import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { API_BASE_URL, API_ENDPOINTS } from '../config/api';
import { ResendVerification } from './ResendVerification';
import { Button } from './ui/button';
import { CONTACT } from '../config/contact';

export function EmailVerification() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  
  // Initialize state from sessionStorage if available
  const getStoredVerificationState = () => {
    if (!token) return { status: 'loading' as const, message: '' };
    
    const stored = sessionStorage.getItem(`verification_${token}`);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return { status: 'loading' as const, message: '' };
      }
    }
    return { status: 'loading' as const, message: '' };
  };

  const initialState = getStoredVerificationState();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>(initialState.status);
  const [message, setMessage] = useState(initialState.message);

  
  const [showResendForm, setShowResendForm] = useState(false);
  
  // Check if verification has been attempted (persists across remounts)
  const getVerificationAttempted = () => {
    if (!token) return false;
    return sessionStorage.getItem(`verification_attempted_${token}`) === 'true';
  };
  
  const setVerificationAttempted = (attempted: boolean) => {
    if (!token) return;
    if (attempted) {
      sessionStorage.setItem(`verification_attempted_${token}`, 'true');
    } else {
      sessionStorage.removeItem(`verification_attempted_${token}`);
    }
  };

  // Store verification state in sessionStorage
  const updateVerificationState = (newStatus: 'loading' | 'success' | 'error', newMessage: string) => {
    setStatus(newStatus);
    setMessage(newMessage);
    
    if (token) {
      sessionStorage.setItem(`verification_${token}`, JSON.stringify({
        status: newStatus,
        message: newMessage
      }));
    }
  };

  useEffect(() => {
    let isMounted = true; // Flag to prevent state updates if component unmounts

    const verifyEmail = async (): Promise<void> => {
      // Check if we already have a stored result
      if (token) {
        const stored = sessionStorage.getItem(`verification_${token}`);
        if (stored) {
          try {
            const storedState = JSON.parse(stored);
            if (storedState.status === 'success' || storedState.status === 'error') {
              // If we have a success result, set up redirect
              if (storedState.status === 'success' && isMounted) {
                setTimeout(() => {
                  if (isMounted) {
                    navigate('/');
                  }
                }, 3000);
              }
              return;
            }
          } catch (e) {
            // Error parsing stored state
          }
        }
      }

      // Prevent duplicate calls (important for React StrictMode)
      if (getVerificationAttempted()) {
        return;
      }

      // Mark as attempted immediately to prevent duplicate calls
      setVerificationAttempted(true);

      if (!token) {
        if (isMounted) {
          updateVerificationState('error', 'Invalid verification link');
        }
        return;
      }

      try {
        const apiUrl = `${API_BASE_URL}${API_ENDPOINTS.AUTH.VERIFY_EMAIL(token)}`;
        
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include', // honor any auth cookie the server may set
        });

        const data = await response.json();

        // Always update sessionStorage, regardless of component mount status
        if (response.ok) {
          const successMessage = data.message || 'Email verified successfully!';
          
          // Always store the result in sessionStorage
          if (token) {
            sessionStorage.setItem(`verification_${token}`, JSON.stringify({
              status: 'success',
              message: successMessage
            }));
          }
          
          // Only update React state if component is still mounted
          if (isMounted) {
            setStatus('success');
            setMessage(successMessage);
            toast.success('Email verified successfully! You can now log in.');
            
            // Redirect to home page after 3 seconds
            setTimeout(() => {
              if (isMounted) {
                navigate('/');
              }
            }, 3000);
          }
        } else {
          const errorMessage = data.message || 'Verification failed';
          
          // Always store the result in sessionStorage
          if (token) {
            sessionStorage.setItem(`verification_${token}`, JSON.stringify({
              status: 'error',
              message: errorMessage
            }));
          }
          
          // Only update React state if component is still mounted
          if (isMounted) {
            setStatus('error');
            setMessage(errorMessage);
            toast.error(errorMessage);
          }
        }
      } catch (error) {
        console.error('Verification error:', error);
        if (isMounted) {
          updateVerificationState('error', 'An error occurred during verification');
          toast.error('An error occurred during verification');
        }
      }
    };

    // Only run verification once when component mounts
    verifyEmail();
    
    // Set up a small interval to check for updated results in sessionStorage
    // This handles the case where the API response comes back after component remount
    const checkInterval = setInterval(() => {
      if (token) {
        const stored = sessionStorage.getItem(`verification_${token}`);
        if (stored) {
          try {
            const storedState = JSON.parse(stored);
            if ((storedState.status === 'success' || storedState.status === 'error') && 
                storedState.status !== status) {
              setStatus(storedState.status);
              setMessage(storedState.message);
              
              // Set up redirect for success
              if (storedState.status === 'success' && isMounted) {
                toast.success('Email verified successfully! You can now log in.');
                setTimeout(() => {
                  if (isMounted) {
                    navigate('/');
                  }
                }, 3000);
              }
              
              clearInterval(checkInterval);
            }
          } catch (e) {
            // Error parsing stored state in interval
          }
        }
      }
    }, 500); // Check every 500ms

    return () => {
      isMounted = false; // Cleanup function
      clearInterval(checkInterval); // Clear the storage check interval
    };
  }, [token, navigate]); // Removed 'status' from dependency array to prevent re-runs

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md">
        <div className="bg-white py-8 px-6 shadow-lg rounded-lg sm:px-10">
          <div className="text-center" role="status" aria-live="polite">
            {status === 'loading' && (
              <>
                <div className="mb-4">
                  <Loader2 className="mx-auto h-16 w-16 text-blue-600 animate-spin" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Verifying your email...
                </h2>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <p className="text-sm text-blue-800 font-medium">
                    Please wait while we verify your email address.
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    This should only take a few moments.
                  </p>
                </div>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="mb-4">
                  <CheckCircle className="mx-auto h-16 w-16 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Email Verified Successfully!
                </h2>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <p className="text-sm text-green-800 font-medium">
                    {message || 'Your email has been verified successfully!'}
                  </p>
                  <p className="text-xs text-green-600 mt-1">
                    You can now access all features of your account.
                  </p>
                </div>
                <p className="text-sm text-gray-500 mb-4">
                  You will be redirected to the home page in a few seconds...
                </p>
                <Button variant="gold" className="w-full" onClick={() => navigate('/')}>
                  Continue to Home Page
                </Button>
              </>
            )}

            {status === 'error' && !showResendForm && (
              <>
                <div className="mb-4">
                  <XCircle className="mx-auto h-16 w-16 text-red-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Verification Failed
                </h2>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <p className="text-sm text-red-800 font-medium">
                    {message || 'Unable to verify your email address.'}
                  </p>
                  <p className="text-xs text-red-600 mt-1">
                    The verification link may have expired or is invalid.
                  </p>
                </div>
                <div className="space-y-3">
                  <Button variant="gold" className="w-full" onClick={() => setShowResendForm(true)}>
                    Resend Verification Email
                  </Button>
                  <Button variant="outline" className="w-full" onClick={() => navigate('/')}>
                    Go to Home Page
                  </Button>
                  <p className="text-xs text-gray-500 text-center mt-4">
                    If you continue to have issues, please contact us at{' '}
                    <a href={`mailto:${CONTACT.emails.general}`} className="text-[#d4af37] hover:underline">
                      {CONTACT.emails.general}
                    </a>.
                  </p>
                </div>
              </>
            )}

            {status === 'error' && showResendForm && (
              <ResendVerification onClose={() => setShowResendForm(false)} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
